import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { categories, getComicsByCategory, getLatestUpdates } from '@/mocks/comics';

export default function Category() {
  const { slug = 'hanman' } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryName = currentCategory?.name || slug;

  const comics = useMemo(() => {
    let result = getComicsByCategory(slug);
    if (result.length === 0) {
      result = getLatestUpdates();
    }
    switch (sortBy) {
      case 'popular':
        return [...result].sort((a, b) => b.views - a.views);
      case 'rating':
        return [...result].sort((a, b) => b.rating - a.rating);
      default:
        return [...result].sort((a, b) => b.updateDate.localeCompare(a.updateDate));
    }
  }, [slug, sortBy]);

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: t('seo.categoryTitle', { category: categoryName }),
    description: t('seo.categoryDesc', { category: categoryName }),
    url: `https://www.qimiaomanhua.com/category/${slug}`,
  };

  return (
    <>
      <SEO
        title={t('seo.categoryTitle', { category: categoryName })}
        description={t('seo.categoryDesc', { category: categoryName })}
        canonicalUrl={`https://www.qimiaomanhua.com/category/${slug}`}
        schema={pageSchema}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="py-4">
            <Breadcrumb
              items={[
                { label: t('breadcrumb.home'), href: '/' },
                { label: t('breadcrumb.category'), href: '/category/hanman' },
                { label: categoryName },
              ]}
            />
          </div>

          {/* Title */}
          <div className="py-6">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              {categoryName}{t('nav.category')}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              {t('category.resultCount', { count: comics.length })}
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-6">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-xs`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Sort */}
          <div className="flex items-center gap-2 mb-6">
            <span className="text-sm text-foreground-500 whitespace-nowrap">{t('category.sortBy')}:</span>
            <div className="flex items-center gap-1">
              {[
                { key: 'latest' as const, label: t('category.sortLatest') },
                { key: 'popular' as const, label: t('category.sortPopular') },
                { key: 'rating' as const, label: t('category.sortRating') },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                    sortBy === opt.key
                      ? 'bg-secondary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Comics Grid */}
          {comics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5 pb-14">
              {comics.map((comic, idx) => (
                <div key={comic.id} className="animate-fade-in-up" style={{ animationDelay: `${idx * 0.04}s` }}>
                  <ComicCard comic={comic} />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4 block"></i>
              <p className="text-foreground-500">{t('category.noResult')}</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}